<template lang="html">
  <v-content>
    <input
      type="file"
      style="display: none"
      ref="selectedDocument"
      @change="onDocumentPicked"
    />

    <!-- Dialog: Manage Target Sheets -->
    <ManageTargetSheetsDialog :opened.sync="manageTargetSheetsDialogOpened" />

    <!-- Dialog: Edit Document Tag -->
    <EditDocumentTagDialog :opened.sync="editDocumentTagDialogOpened" />

    <v-dialog
      width="550"
      v-model="syncSharepointIntegrationDialogOpened"
      @keydown.esc="syncSharepointIntegrationDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title py-3">
          <v-layout align-start
            >Would you like to sync your SharePoint account?</v-layout
          >

          <v-spacer />
        </v-card-title>

        <v-card-text class="py-3 text-xs-left">
          All folders and files currently in Soraban will be synced over to your
          SharePoint account.
        </v-card-text>

        <v-card-text class="py-1 text-xs-left">
          This will take awhile but we'll notify you when it's finished.
        </v-card-text>

        <v-card-actions>
          <v-spacer />
          <v-btn
            flat
            color="error"
            class="text-capitalize"
            @click="syncSharepointIntegrationDialogOpened = false"
          >
            Cancel
          </v-btn>
          <v-btn
            class="text-capitalize"
            color="primary"
            @click="startSharepointSync()"
            >Start Sync</v-btn
          >
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Upload Report Template -->
    <v-dialog
      width="550"
      v-model="uploadReportTemplateDialogOpened"
      @keydown.esc="uploadReportTemplateDialogOpened = false"
    >
      <v-card flat @drop.prevent="addFile" @dragover.prevent>
        <v-card-title class="title pb-0 text-xs-left">
          Upload a report template
        </v-card-title>
        <v-card-text>
          <v-container
            @drop.prevent="addFile"
            @dragover.prevent
            v-if="!documentToImport"
          >
            <v-layout
              class="mt-2 mb-3 drag-and-drop"
              column
              align-center
              justify-center
            >
              <div><v-icon>cloud_upload</v-icon></div>
              <div>
                <span @click="pickDocument"
                  >Drop a file here or
                  <span class="clickable primary--text"
                    >click to select</span
                  ></span
                >
              </div>
            </v-layout>
          </v-container>
          <v-flex class="mb-4" v-else>
            <v-layout align-center>
              <v-icon color="primary"> insert_drive_file </v-icon>
              <v-text-field
                class="default-input ml-1"
                v-model="documentToImport.name"
                solo
                hide-details
              />
              <v-spacer />
              <v-icon
                @click="documentToImport = null"
                style="cursor: pointer"
                color="error"
                >cancel</v-icon
              >
            </v-layout>
          </v-flex>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="text-capitalize primary"
            @click="uploadReportTemplate()"
            :loading="documentImporting"
            :disabled="!documentToImport"
          >
            Upload
          </v-btn>
          <v-btn
            class="text-capitalize primary--text"
            outline
            @click="
              uploadReportTemplateDialogOpened = false;
              documentToImport = null;
            "
          >
            {{ $t('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Add Webhook -->
    <add-webhook-dialog
      :opened.sync="addWebhookDialogOpened"
      @added-webhook="getAllWebhooks()"
    />

    <!-- Dialog: Create Custom Report -->
    <create-custom-report-dialog
      :opened.sync="createCustomReportDialogOpened"
      :custom-templates="reportTemplates"
      multiple
    />

    <!-- Dialog: Manage Tags -->
    <ManageTagsDialog
      :opened.sync="manageTagsDialogOpened"
      :tagType.sync="tagType"
    />

    <!-- Dialog: Manage Group Dialogs -->
    <v-dialog
      v-model="manageGroupsDialogOpened"
      :fullscreen="$vuetify.breakpoint.xsOnly"
      max-width="600"
      persistent
      @keydown.esc="manageGroupsDialogOpened = false"
    >
      <v-card>
        <v-card-title class="title pb-0">
          Manage Firm Groups
          <v-spacer />
          <v-btn
            fab
            small
            class="error--text text-none my-0"
            flat
            @click="manageGroupsDialogOpened = false"
          >
            <v-icon>clear</v-icon>
          </v-btn>
        </v-card-title>
        <manage-groups />
      </v-card>
    </v-dialog>

    <!-- Dialog: Sender Email Address Confirmation -->
    <v-dialog
      :fullscreen="$vuetify.breakpoint.xsOnly"
      v-model="senderEmailAddressConfirmationDialogOpened"
      width="600"
    >
      <v-card block flat>
        <v-card-title class="title"> Important </v-card-title>
        <v-card-text class="text-xs-left subheading">
          Accountants are required to connect their Gmail accounts to send email
          using their email addresses. It will fallback to use Soraban's email
          address if the Gmail account is not connected.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn
            class="primary text-none"
            flat
            @click="senderEmailAddressConfirmationDialogOpened = false"
          >
            Okay
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <sc-flex :is-column="true" :gap="16">
      <!-- Partial: Header -->
      <sc-flex :gap="16" :is-y-centered="true" :is-between="true">
        <sc-h1>Manage Firm</sc-h1>
        <sc-button-group>
          <sc-button
            v-if="currentFirm.document_tags_enabled"
            @click="editDocumentTagDialogOpened = true"
            type="ghost"
          >
            Manage Document Tags
          </sc-button>
          <sc-button
            v-if="currentFirm.document_tags_enabled"
            @click="manageGroupsDialogOpened = true"
            type="ghost"
          >
            Manage Contact Tags
          </sc-button>
          <sc-button
            v-if="currentFirm && currentFirm.has_firm_groups"
            @click="manageGroupsDialogOpened = true"
            type="ghost"
          >
            Manage Firm Groups
          </sc-button>
        </sc-button-group>
      </sc-flex>

      <!-- Partial: Content -->
      <v-card>
        <v-card-text>
          <!-- Partial: Tabs -->
          <v-tabs
            v-model="activeTab"
            color="white"
            slider-color="primary"
            class="mb-4"
          >
            <v-tab
              key="firm"
              ripple
              class="text-none subheading"
              href="#firm-settings"
              @click="activeTab = 'firm-settings'"
            >
              Firm Settings
            </v-tab>
            <v-tab
              key="users"
              ripple
              class="text-none subheading"
              ref="firmUsersTab"
              href="#firm-users"
              @click="activeTab = 'firm-users'"
            >
              Firm Users
            </v-tab>
            <v-tab
              key="email"
              ripple
              class="text-none subheading"
              ref="integrationsTab"
              href="#integrations"
              @click="activeTab = 'integrations'"
            >
              <span>
                Integrations
              </span>
            </v-tab>
            <v-tab
              key="reports"
              ripple
              class="text-none subheading"
              href="#reports"
              @click="activeTab = 'reports'"
            >
              Reports
            </v-tab>
            <v-tab
              key="glossary"
              ripple
              class="text-none subheading"
              ref="glossaryTab"
              v-if="firmGlossaryEnabled"
              href="#glossary"
              @click="activeTab = 'glossary'"
            >
              Glossary
            </v-tab>
            <v-tab
              key="ops"
              ripple
              class="text-none subheading"
              ref="opsTab"
              href="#operations"
              @click="activeTab = 'operations'"
            >
              Rollover Items
            </v-tab>
            <v-tab
              key="personalization"
              ripple
              class="text-none subheading"
              ref="personalizationTab"
              href="#personalization"
              @click="activeTab = 'personalization'"
            >
              Preferences
            </v-tab>
            <v-tab
              key="custom-statuses"
              ripple
              class="text-none subheading"
              ref="customStatusesTab"
              v-if="customStatusesEnabled"
              href="#custom-statuses"
              @click="activeTab = 'custom-statuses'"
            >
              Custom Statuses
            </v-tab>
          </v-tabs>

          <!-- Partial: Content -->
          <!-- Item: Firm Users -->
          <div v-if="activeTab === 'firm-users'">
            <v-layout>
              <firm-users-tab
                :current-user-firm="currentFirm"
                :loading="loadingUsers"
                :firm-users="firmUsers"
                @refresh="getAllFirmUsers()"
              />
            </v-layout>
          </div>

          <!-- Item: Integrations -->
          <v-layout v-else-if="activeTab === 'integrations'" class="pa-2">
            <v-layout column>
              <div class="bordered-container">
                <sc-flex :is-between="true" :is-y-centered="true">
                  <sc-h3>Webhooks</sc-h3>
                  <sc-button-group>
                    <sc-button
                      type="secondary"
                      @click="addWebhookDialogOpened = true"
                      data-onboarding="true"
                    >
                      Add webhook
                    </sc-button>
                  </sc-button-group>
                </sc-flex>

                <v-data-table
                  :headers="webhookHeaders"
                  :items="webhooks"
                  :loading="webhooksTableLoading"
                  hide-actions
                  class="text-xs-left"
                >
                  <template slot="headerCell" slot-scope="props">
                    <template>
                      <span>
                        {{ props.header.text }}
                      </span>
                    </template>
                  </template>

                  <template slot="items" slot-scope="props">
                    <tr :key="props.index" slot="header">
                      <td class="text-xs-left">
                        {{ props.item.url }}
                      </td>
                      <td class="text-xs-left">
                        {{ props.item.hook_type }}
                      </td>
                      <td clas="text-center">
                        <v-layout align-center>
                          <v-menu
                            transition="slide-y-transition"
                            bottom
                            nudge-bottom="30"
                            class="my-1"
                          >
                            <v-icon slot="activator"> more_vert </v-icon>
                            <v-list>
                              <v-list-tile
                                @click="deleteWebhook(props.item.id)"
                              >
                                <v-list-tile-title>
                                  Delete webhook
                                </v-list-tile-title>
                              </v-list-tile>
                            </v-list>
                          </v-menu>
                        </v-layout>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </div>

              <div class="bordered-container mt-4">
                <sc-h3>Integrations</sc-h3>

                <v-layout class="pa-3" column>
                  <v-layout row wrap align-center>
                    <v-flex xs2 mt-4>
                      <img
                        src="@/assets/integrations/zapier.png"
                        height="50"
                        width="50"
                      />
                      <div class="subheading">Zapier</div>
                    </v-flex>

                    <v-flex class="text-xs-left" xs6>
                      Integrate Soraban notifications with Zapier so you can
                      take automation to the next level.
                    </v-flex>

                    <v-flex xs4 v-if="selectedIntegration === null">
                      <v-btn
                        class="primary text-none"
                        @click="
                          getOrCreateZapierAPIKey();
                          selectedIntegration = 'zapier';
                        "
                      >
                        Show API Key
                      </v-btn>
                    </v-flex>

                    <v-flex xs4 v-if="selectedIntegration === 'zapier'">
                      <v-alert
                        :value="true"
                        outline
                        block
                        :icon="$vuetify.breakpoint.smAndUp ? 'info' : ''"
                        color="info"
                        class="text-xs-left mb-2"
                      >
                        Copy the below API key into Zapier.
                      </v-alert>
                      <v-layout align-center>
                        <v-text-field
                          v-model="zapierAPIKey"
                          ref="templateLink"
                          disabled
                          class="default-input"
                          solo
                          hide-details
                        />
                      </v-layout>
                    </v-flex>
                  </v-layout>

                  <v-layout v-if="billingEnabled" mt-4 row wrap align-center>
                    <v-flex xs2>
                      <img
                        src="@/assets/integrations/stripe.svg"
                        height="100"
                        width="100"
                      />
                      <div class="subheading">Stripe Express</div>
                    </v-flex>

                    <v-flex class="text-xs-left" xs6>
                      Seamlessly create and integrate a new Stripe account
                      (select this option if you do not need direct access to
                      Stripe).
                    </v-flex>

                    <v-flex xs4>
                      <v-btn
                        class="primary text-none"
                        :loading="loadingStripe"
                        :color="stripeExpressConnected ? 'success' : 'primary'"
                        :disabled="
                          stripeExpressPending || stripeStandardConnected
                        "
                        @click="startStripeIntegration()"
                      >
                        <v-icon left small v-if="stripeExpressConnected">
                          check
                        </v-icon>
                        {{
                          stripeExpressPending
                            ? 'Pending verification'
                            : stripeExpressConnected
                            ? 'Connected'
                            : 'Connect'
                        }}
                      </v-btn>

                      <p v-if="stripeStandardConnected">
                        Please contact support to disconnect your Stripe
                        Standard account before connecting your Stripe Express
                        account.
                      </p>
                    </v-flex>
                  </v-layout>

                  <v-layout v-if="billingEnabled" mt-4 row wrap align-center>
                    <v-flex xs2>
                      <img
                        src="@/assets/integrations/stripe.svg"
                        height="100"
                        width="100"
                      />
                      <div class="subheading">Standard Stripe Account</div>
                    </v-flex>

                    <v-flex class="text-xs-left" xs6>
                      Connect your existing Stripe account to Soraban and
                      maintain full control over your account.
                    </v-flex>

                    <v-flex xs4>
                      <v-btn
                        class="primary text-none"
                        :loading="loadingStripe"
                        :color="stripeStandardConnected ? 'success' : 'primary'"
                        :disabled="stripeExpressConnected"
                        @click="goToStripeOAuth()"
                      >
                        <v-icon left small v-if="stripeStandardConnected">
                          check
                        </v-icon>
                        {{ stripeStandardConnected ? 'Connected' : 'Connect' }}
                      </v-btn>
                      <div class="subheading" v-if="stripeExpressConnected">
                        Please contact support to disconnect your Stripe Express
                        account before connecting your existing Stripe account.
                      </div>
                    </v-flex>
                  </v-layout>

                  <v-layout
                    mt-4
                    align-center
                    row
                    class="one-drive-integration-layout"
                  >
                    <v-flex xs2>
                      <img
                        :src="require('@/assets/microsoft.png')"
                        height="40"
                        width="40"
                      />
                      <div class="subheading">SharePoint</div>
                    </v-flex>

                    <v-flex class="text-xs-left" xs6>
                      Reach out to us at
                      <a href="mailto:hello@soraban.com">hello@soraban.com</a>
                      to begin the process of integrating with SharePoint. Learn
                      more
                      <a
                        href="https://whimsical-split-479.notion.site/Sharepoint-Integration-Beta-bac60f5462e54e5cb63e51ad46e1de51"
                        target="_blank"
                        >here</a
                      >.
                    </v-flex>

                    <v-flex
                      v-if="
                        sharepointIntegrationEnabled && sharepointFirmConnected
                      "
                      xs4
                    >
                      <v-tooltip :disabled="!startedSharepointSync" bottom>
                        <v-btn
                          class="primary text-none"
                          :color="primary"
                          :disabled="startedSharepointSync"
                          slot="activator"
                          @click="syncSharepointIntegrationDialogOpened = true"
                        >
                          Sync SharePoint Account
                        </v-btn>
                        <span>Sync has already been started</span>
                      </v-tooltip>
                    </v-flex>

                    <v-flex xs4>
                      <v-btn
                        class="primary text-none"
                        :color="
                          sharepointPersonallyConnected ? 'success' : 'primary'
                        "
                        :loading="loadingSharepoint"
                        :disabled="
                          !sharepointIntegrationEnabled || loadingSharepoint
                        "
                        @click="connectMicrosoftAccount()"
                      >
                        <v-icon left small v-if="sharepointPersonallyConnected">
                          check
                        </v-icon>
                        {{
                          sharepointPersonallyConnected
                            ? 'Connected'
                            : 'Connect'
                        }}
                      </v-btn>
                    </v-flex>
                  </v-layout>

                  <v-layout
                    mt-4
                    align-center
                    row
                    wrap
                    v-if="scanAndAutoFlowExportEnabled"
                  >
                    <v-flex xs2>
                      <img
                        src="@/assets/integrations/axcess.png"
                        height="50"
                        width="50"
                      />
                      <div class="subheading">CCH Scan & AutoFlow</div>
                    </v-flex>

                    <v-flex class="text-xs-left" xs6>
                      Target Sheets integration for CCH Scan and AutoFlow.
                    </v-flex>

                    <v-flex xs4>
                      <v-btn
                        class="primary text-none"
                        @click="manageTargetSheetsDialogOpened = true"
                      >
                        Manage Target Sheets
                      </v-btn>
                    </v-flex>
                  </v-layout>
                </v-layout>
              </div>
            </v-layout>
          </v-layout>

          <!-- Item: Reporsts -->
          <div v-else-if="activeTab === 'reports'">
            <v-layout column>
              <template v-if="customReportsEnabled">
                <sc-flex :is-between="true" :is-y-centered="true">
                  <sc-h1>Custom Reports</sc-h1>
                  <sc-button-group>
                    <sc-button
                      type="secondary"
                      @click="uploadReportTemplateDialogOpened = true"
                      data-onboarding="true"
                    >
                      Upload Template
                    </sc-button>
                    <sc-button
                      type="primary"
                      @click="createCustomReportDialogOpened = true"
                    >
                      Generate
                    </sc-button>
                  </sc-button-group>
                </sc-flex>

                <v-alert
                  outline
                  type="info"
                  :value="true"
                  class="text-xs-left mb-4"
                >
                  You can fill in the cell value with variables. Lists of are:
                  client_full_name, client_fname, client_lname, client_email,
                  client_phone_number, entity_name, total_tax,
                  adjusted_gross_income, total_income, total_assets,
                  taxable_income, total_wages, withholding, w2_witholdings,
                  refund_amount, overpayment_amount, refund_method,
                  applied_amount.
                </v-alert>

                <v-data-table
                  :headers="reportTemplateHeaders"
                  :items="reportTemplates"
                  :loading="reportTemplatesLoading"
                  hide-actions
                  class="text-xs-left mb-4"
                >
                  <template slot="headerCell" slot-scope="props">
                    <template>
                      <span>
                        {{ props.header.text }}
                      </span>
                    </template>
                  </template>
                  <template slot="items" slot-scope="props">
                    <tr :key="props.index" slot="header">
                      <td class="text-xs-left">
                        {{ props.item.name }}
                      </td>
                      <td class="text-xs-center">
                        <v-menu
                          transition="slide-y-transition"
                          bottom
                          nudge-bottom="30"
                          class="my-1"
                        >
                          <v-icon slot="activator"> more_vert </v-icon>
                          <v-list>
                            <v-list-tile @click="downloadDocument(props.item)">
                              <v-list-tile-title> Download </v-list-tile-title>
                            </v-list-tile>
                            <v-list-tile @click="deleteDocument(props.item)">
                              <v-list-tile-title> Delete </v-list-tile-title>
                            </v-list-tile>
                          </v-list>
                        </v-menu>
                      </td>
                    </tr>
                  </template>
                </v-data-table>
              </template>

              <v-layout align-center justify-space-between>
                <div class="title">Firm Reports</div>
              </v-layout>
              <v-layout align-center>
                <div class="subheading">• All Clients Report</div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateClientList()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center>
                <div class="subheading">• All Entities Report</div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateEntityList()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center>
                <div class="subheading">
                  • All Entities with Conversation Report
                </div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateEntityListWithConversation()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center v-if="customReportsEnabled">
                <div class="subheading">• All Tax Data Report</div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateAllTaxDataReport()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center v-if="signatureRequestsEnabled">
                <div class="subheading">• All Signature Requests Report</div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateSignatureRequestReport()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center v-if="deliveryEnabled">
                <div class="subheading">• Delivery Report</div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateDeliveryReport()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center justify-space-between>
                <div class="subheading">• Questionnaire Report</div>
                <v-spacer />
                <span>Year</span>
                <v-select
                  :items="yearOptionsWithAll"
                  v-model="selectedQuestionnaireYear"
                  solo
                  hide-details
                  class="default-input"
                  style="max-width: 150px"
                />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateQuestionnaireReport()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout align-center justify-space-between>
                <div class="subheading-container">
                  <div class="subheading">
                    • Nearly Completed by Client Questionnaire Report
                  </div>
                  <div class="subheading-explanation">
                    Generate a report of incomplete questionnaires that are
                    nearly completed by the client, through the percentage of
                    sections completed.
                  </div>
                </div>
                <v-spacer />
                <div>
                  <span>Year</span>
                  <v-select
                    :items="yearOptionsWithAll"
                    v-model="selectedQuestionnaireYear"
                    solo
                    hide-details
                    class="default-input"
                    style="max-width: 150px"
                  />
                </div>
                <div>
                  <span>Percentage</span>
                  <v-select
                    :items="percentageOptions"
                    v-model="selectedQuestionnairePercentage"
                    solo
                    hide-details
                    class="default-input"
                    style="max-width: 100px"
                  />
                </div>
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateNearlyFinishedQuestionnaireReport()"
                  >Generate</v-btn
                >
              </v-layout>
              <!--Generate Unseen Mesasges Report-->
              <v-layout align-center>
                <div class="subheading-container">
                  <div class="subheading">• Unseen Messages</div>
                  <div class="subheading-explanation">
                    Message sent by client which had no response for over 48
                    hours.
                  </div>
                </div>
                <v-spacer />
                <v-btn
                  class="primary text-none font-weight-bold"
                  @click="generateUnseenMessagesReport()"
                  >Generate</v-btn
                >
              </v-layout>
              <v-layout column v-if="creditUsageEnabled || isSorabanAdmin">
                <v-layout align-center justify-space-between class="mt-4">
                  <div class="title">
                    Credit Usage<v-chip small v-if="!creditUsageEnabled"
                      >Soraban Admin Only</v-chip
                    >
                  </div>
                  <v-spacer />
                  <span>Tax Year</span>
                  <v-select
                    :items="yearOptions"
                    v-model="selectedYear"
                    solo
                    hide-details
                    class="default-input"
                    style="max-width: 150px"
                  />
                  <v-btn
                    class="primary text-none font-weight-bold"
                    @click="generateUsageReport()"
                    >Generate Report</v-btn
                  >
                </v-layout>
                <v-layout column align-center v-if="creditUsageLoading">
                  <v-progress-circular
                    indeterminate
                    size="40"
                    color="primary"
                  ></v-progress-circular>
                </v-layout>

                <!--Customer Billing Dashboard-->
                <v-layout column v-if="!creditUsageLoading">
                  <CustomerBillingDashboard
                    :credits="creditUsage"
                    :selected-year="selectedYear"
                  ></CustomerBillingDashboard>
                </v-layout>
              </v-layout>
            </v-layout>
          </div>

          <!-- Item: Glossary -->
          <div v-else-if="activeTab === 'glossary'">
            <FirmGlossaryTab :firm-id="currentFirm.id" />
          </div>

          <!-- Item: Operations -->
          <div v-else-if="activeTab === 'operations'">
            <FirmOperationsTab :firm-id="currentFirm.id" />
          </div>

          <!-- Item: Personalization -->
          <div v-else-if="activeTab === 'personalization'">
            <PersonalizationTab :firm="currentFirm" />
          </div>

          <!-- Item: Custom Statuses -->
          <div v-else-if="activeTab === 'custom-statuses'">
            <CustomStatusesTab :firm="currentFirm" />
          </div>

          <!-- Default Item: Firm Settings -->
          <div v-else>
            <v-layout align-start wrap>
              <v-flex xs12 sm6 class="px-2 mb-4">
                <div class="text-xs-left dashboard__label">Firm Logo</div>
                <form id="logo-form" @submit.prevent="submit">
                  <input
                    type="file"
                    style="display: none"
                    ref="selectedLogo"
                    @change="onFileChange"
                    name="logo"
                    accept="image/*"
                  />
                </form>
                <v-layout align-center justify-start>
                  <img
                    v-if="currentFirm.thumb_url"
                    :src="currentFirm.thumb_url"
                    class="logo firm-logo"
                  />
                  <v-btn
                    id="select-logo-btn"
                    class="ml-2 default-input primary text-capitalize"
                    @click="selectImage()"
                    :loading="loading"
                  >
                    {{ currentFirm.thumb_url ? 'Change Logo' : 'Select Logo' }}
                  </v-btn>
                </v-layout>
              </v-flex>

              <v-flex xs12 sm6 class="pt-2 pb-0">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Firm Name</div>
                </v-layout>
                <v-text-field
                  name="firm-name"
                  label=""
                  type="text"
                  v-model="currentFirm.name"
                  solo
                  hide-details
                  class="default-input mb-0"
                  disabled
                />
                <v-switch
                  class="default-input ma-0 pa-0"
                  v-model="hideFirmNameFromEmailValue"
                  @change="updateHideName"
                  solo
                  hide-details
                  label="Hide firm name from email"
                />
              </v-flex>

              <v-flex xs12 sm6 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Send Email As</div>
                </v-layout>
                <v-select
                  name="firm-name"
                  v-model="currentFirm.default_email_user_id"
                  :items="firmUsersWithNull"
                  solo
                  item-text="name"
                  item-value="id"
                  class="default-input"
                  @change="updateFirm()"
                />
              </v-flex>

              <v-flex xs12 sm6 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Fallback Email</div>
                  <v-tooltip top>
                    <v-layout align-center slot="activator">
                      <v-icon size="15" class="ml-2">info</v-icon>
                    </v-layout>
                    <span
                      >By default, Soraban will use user's connected account
                      (Outlook/Gmail) to send email. If that fails, the fallback
                      email will be used instead.</span
                    >
                  </v-tooltip>
                </v-layout>
                <v-select
                  name="firm-name"
                  v-model="currentFirm.default_sender_email_address"
                  :items="selectableEmailAddresses"
                  solo
                  item-text="name"
                  item-value="value"
                  class="default-input"
                  @change="updateSenderEmailAddress"
                />
              </v-flex>

              <v-flex xs12 sm6 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Firm support email</div>
                </v-layout>

                <v-text-field
                  name="firm-support-email"
                  type="email"
                  :value="firmSupportEmail"
                  solo
                  hide-details
                  placeholder="e.g. support@soraban.com"
                  class="default-input"
                  @blur="updateSupportEmail"
                />

                <v-switch
                  class="default-input ma-0 pa-0"
                  v-model="hideHelloSorabanFromEmails"
                  @change="updateHideHelloSorabanFromEmails"
                  solo
                  hide-details
                  label="Hide hello@soraban.com from questionnaire emails"
                />
              </v-flex>

              <v-flex xs12 sm6 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Firm phone number</div>
                </v-layout>

                <v-text-field
                  name="firm-phone-number"
                  type="text"
                  mask="phone"
                  :value="firmPhoneNumber"
                  solo
                  placeholder="e.g. (123) 456 - 7890"
                  hide-details
                  class="default-input"
                  @blur="updatePhoneNumber"
                />

                <v-switch
                  class="default-input ma-0 pa-0"
                  v-model="showPhoneNumberValue"
                  @change="updateShowPhoneNumber"
                  :true-value="true"
                  :false-value="false"
                  solo
                  hide-details
                  label="Show phone number in questionnaire"
                />
              </v-flex>

              <v-flex xs12 sm6 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Time Zone</div>
                  <v-tooltip top>
                    <v-layout align-center slot="activator">
                      <v-icon size="15" class="ml-2">info</v-icon>
                    </v-layout>
                    <span
                      >This is the default time zone for new clients and
                      scheduled questionnaires</span
                    >
                  </v-tooltip>
                </v-layout>
                <v-select
                  v-model="currentFirm.time_zone"
                  solo
                  :items="timeZones"
                  @change="updateFirm()"
                  class="default-input mb-1"
                />
              </v-flex>
              <v-flex xs12 sm6 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Reminder frequency</div>
                  <v-tooltip top>
                    <v-layout align-center slot="activator">
                      <v-icon size="15" class="ml-2">info</v-icon>
                    </v-layout>
                    <span
                      >Automated reminder frequency in days for all
                      questionnaires in firm that hasn't been completed or past
                      due date.</span
                    >
                  </v-tooltip>
                </v-layout>
                <v-text-field
                  name="questionnaire_reminder_frequency"
                  type="number"
                  :value="questionnaireReminderFrequency"
                  solo
                  placeholder="Defaults to 7 days"
                  hide-details
                  class="default-input mb-4"
                  @blur="updateReminderFrequency"
                />
              </v-flex>
              <v-flex xs12 class="px-2">
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Welcome message</div>
                  <v-tooltip top>
                    <v-layout align-center slot="activator">
                      <v-icon size="15" class="ml-2">info</v-icon>
                    </v-layout>
                    <span>
                      This is the welcome message clients will receive in their
                      first login.<br />
                      You can utilize:<br />{{ entityVariables }}<br />{{
                        userVariables
                      }}
                    </span>
                  </v-tooltip>
                </v-layout>
                <v-textarea
                  solo
                  label="Welcome message"
                  :value="customWelcomeMessage"
                  @input="updateCustomWelcomeMessage"
                  class="default-input mb-1"
                  placeholder="Hi {client_preferred_name}, welcome to Soraban!...."
                  hide-details
                />
              </v-flex>
              <v-flex
                xs12
                class="px-2"
                v-if="get(currentFirm, 'preferences.extension_email', null)"
              >
                <v-layout align-center class="text-xs-left">
                  <div class="dashboard__label">Extension email</div>
                  <v-tooltip top>
                    <v-layout align-center slot="activator">
                      <v-icon size="15" class="ml-2">info</v-icon>
                    </v-layout>
                    <span>
                      This is the email the clients will receive regarding
                      filing extension for personal returns.
                    </span>
                  </v-tooltip>
                </v-layout>
                <v-textarea
                  solo
                  label="Extension email"
                  :value="
                    firmExtensionEmail
                      ? firmExtensionEmail
                      : defaultExtensionEmail
                  "
                  @input="updateExtensionEmail"
                  @change="updatePreferences"
                  class="default-input mb-1"
                  hide-details
                />
              </v-flex>
              <v-layout>
                <v-spacer />
                <v-btn
                  class="default-input primary text-capitalize"
                  @click="updatePreferences()"
                >
                  Save
                </v-btn>
              </v-layout>
            </v-layout>
            <v-layout>
              <v-switch
                v-if="isSorabanAdmin"
                v-model="currentFirm.paying"
                @change="updateFirm()"
                hide-details
                class="default-input my-0 mr-1"
                :label="currentFirm.paying ? 'Customer Account' : 'Test'"
              />
              <v-spacer />
              <v-layout column align-end v-if="!currentFirm.paying">
                <v-btn
                  class="default-input primary--text text-none"
                  @click="downloadAllFolders()"
                  outline
                >
                  Download All Folders in one zipped folder
                </v-btn>
                <v-btn
                  class="default-input primary--text text-none"
                  @click="downloadAllFolders(true)"
                  outline
                >
                  Download All Folders, one zipped folder per Company
                </v-btn>
              </v-layout>
            </v-layout>
          </div>
        </v-card-text>
      </v-card>
    </sc-flex>
  </v-content>
</template>

<script>
import { mapState, mapGetters } from 'vuex';
import API from '@/api';
import isEmpty from 'lodash/isEmpty';
import get from 'lodash/get';
import moment from 'moment';
import ManageGroups from '@/components/ManageGroups';
import CreateCustomReportDialog from '@/components/dialogs/CreateCustomReportDialog';
import AddWebhookDialog from '@/components/dialogs/AddWebhookDialog';
import EditDocumentTagDialog from '@/components/dialogs/EditDocumentTagDialog';
import ManageTagsDialog from '@/components/dialogs/ManageTagsDialog';
import FirmPreferenceMixins from '@/mixins/FirmPreferenceMixins';
import AdminFunctionsMixin from '@/mixins/AdminFunctionsMixin';
import BillingSummary from '@/components/billing/BillingSummary.vue';
import CustomerBillingDashboard from '@/components/CustomerBillingDashboard.vue';
import ManageTargetSheetsDialog from '@/components/dialogs/ManageTargetSheetsDialog';

import {
  timeZones,
  yearOptions,
  entityVariables,
  userVariables,
} from '@/constants/variables';
import FirmGlossaryTab from './FirmGlossaryTab';
import FirmOperationsTab from './FirmOperationsTab';
import FirmUsersTab from './FirmUsersTab';
import PersonalizationTab from './PersonalizationTab';
import CustomStatusesTab from './CustomStatusesTab';

export default {
  name: 'FirmSettings',
  components: {
    CustomStatusesTab,
    PersonalizationTab,
    FirmUsersTab,
    FirmGlossaryTab,
    FirmOperationsTab,
    ManageTagsDialog,
    ManageGroups,
    CreateCustomReportDialog,
    AddWebhookDialog,
    EditDocumentTagDialog,
    BillingSummary,
    CustomerBillingDashboard,
    ManageTargetSheetsDialog,
  },
  mixins: [FirmPreferenceMixins, AdminFunctionsMixin],
  data() {
    return {
      yearOptions,
      entityVariables,
      userVariables,
      hideFirmNameFromEmailValue: false,
      showPhoneNumberValue: false,
      hideHelloSorabanFromEmails: false,
      selectedQuestionnaireYear: 2024,
      selectedQuestionnairePercentage: 0.9,
      creditUsageLoading: true,
      selectedYear: 2024,
      editDocumentTagDialogOpened: false,
      connectAccountDialog: false,
      addWebhookDialogOpened: false,
      uploadReportTemplateDialogOpened: false,
      senderEmailAddresses: [],
      activeTab: 'firm-settings',
      syncSharepointIntegrationDialogOpened: false,
      startedSharepointSync: false,
      senderEmailAddressConfirmationDialogOpened: false,
      createCustomReportDialogOpened: false,
      timeZones,
      firmUsers: [],
      valid: false,
      username: '',
      password: '',
      passwordRules: [v => !!v || 'Password is required'],
      selectedUsers: [],
      fieldRules: [v => !!v || 'This field is required'],
      integrationDialogOpened: false,
      loading: false,
      manageTagsDialogOpened: false,
      selectedIntegration: null,
      zapierAPIKey: null,
      documentData: '',
      documentName: '',
      documentLoading: false,
      ipAddressListDialogOpened: false,
      approvedIPAddressList: [],
      newIPAddress: '',
      rdpPassword: '',
      manageGroupsDialogOpened: false,
      webhookHeaders: [
        {
          text: 'Hook URL',
          align: 'left',
          sortable: true,
          value: 'url',
          width: '50%',
        },
        {
          text: 'Type',
          align: 'left',
          sortable: true,
          value: 'type',
          width: '45%',
        },
        {
          text: 'Actions',
          align: 'center',
          sortable: false,
          width: '5%',
        },
      ],
      reportTemplateHeaders: [
        {
          text: 'Name',
          align: 'left',
          sortable: true,
          value: 'name',
          width: '45%',
        },
        {
          text: 'Actions',
          align: 'left',
          sortable: false,
          width: '5%',
        },
      ],
      linkedAccounts: [],
      webhooks: [],
      documentToImport: null,
      documentImporting: false,
      reportTemplatesLoading: true,
      reportTemplates: [],
      loadingSharepoint: false,
      loadingStripe: false,
      sharepointFirmConnected: false,
      sharepointPersonallyConnected: false,
      stripeExpressConnected: false,
      stripeStandardConnected: false,
      stripeExpressPending: false,
      webhooksTableLoading: false,
      loadingUsers: false,
      defaultExtensionEmail:
        'Hi {clientName},\n<br/><br/>\nSince you have not completed the individual tax filing questionnaire, we are preparing an extension and would like to confirm how you would like to proceed. We have the following options:\n<br/><br/>\n<b>Option 1:</b> Extension without making any payment. This may result in late and underpayment penalties.\n<br/><br/>\n<b>Option 2:</b> Extension with a custom payment. Let us know how much you will be paying, for Federal and State. Payment for less than the amount due may result in late and underpayment penalties.\n<br/><br/>\n<b>Option 3:</b>  Extension based on prior year tax liability. Let us know any estimated tax payments made for 2022, if any.\n<br/><br/>\nClick the button below for us to let us know which option you would like to proceed with. If we don’t receive a response from you by April 11th, we will proceed to file an extension without any payments (option 1).',
      tagType: 'client-tags',
      creditUsage: null,
      manageTargetSheetsDialogOpened: false,
      stripeOAuthURL: null,
    };
  },
  computed: {
    ...mapState('companies', ['companies']),
    ...mapGetters({
      currentUser: 'user',
      currentUserDefaultCompanyId: 'currentUserDefaultCompanyId',
      notifications: 'notifications',
    }),
    yearOptionsWithAll() {
      return ['All', ...this.yearOptions];
    },
    percentageOptions() {
      return [
        { text: '90%', value: 0.9 },
        { text: '80%', value: 0.8 },
        { text: '70%', value: 0.7 },
        { text: '60%', value: 0.6 },
      ];
    },
    logoForm() {
      return this.$el.querySelector('#logo-form');
    },
    firmUsersWithNull() {
      return [
        { name: 'Use Accountant Name', id: null },
        { name: 'Use Firm Name', id: 0 },
      ].concat(this.firmUsers);
    },
    selectableEmailAddresses() {
      return this.linkedAccounts
        .map(account => {
          return {
            name: account.email,
            value: account.email,
          };
        })
        .concat({
          name: 'noreply@withsoraban.com',
          value: null,
        });
    },
  },
  methods: {
    isEmpty,
    get,
    moment,
    async getAllFirmUsers() {
      this.loadingUsers = true;

      try {
        const { data } = await API.firm.getAllUsers();
        this.firmUsers = data.users;
      } catch (error) {
        console.error(error);
      } finally {
        this.loadingUsers = false;
      }
    },
    async startStripeIntegration() {
      try {
        this.loadingStripe = true;
        const response = await API.integration.getStripeAccountLink();

        if (this.isInIframe) {
          window.parent.location = response.data.url;
        } else {
          window.location = response.data.url;
        }
      } catch (error) {
        this.loadingStripe = false;
      }
    },

    goToStripeOAuth() {
      if (this.isInIframe) {
        window.parent.location = this.stripeOAuthURL;
      } else {
        window.location = this.stripeOAuthURL;
      }
    },

    async checkSharepointIntegration() {
      try {
        this.loadingSharepoint = true;
        const response = await API.integration.checkSharepointIntegration();
        this.sharepointPersonallyConnected = response.data.connected;
        this.sharepointFirmConnected = response.data.firm_connected;
        this.loadingSharepoint = false;
      } catch (error) {
        this.loadingSharepoint = false;
      }
    },

    async checkStripeIntegration() {
      try {
        this.loadingStripe = true;
        const response = await API.integration.checkStripeIntegration();

        this.stripeExpressConnected =
          response.data.connected &&
          response.data.stripe_account_type === 'express';
        this.stripeStandardConnected =
          response.data.connected &&
          response.data.stripe_account_type === 'standard';

        this.stripeExpressPending = response.data.pending;
        this.loadingStripe = false;
      } catch (error) {
        this.loadingStripe = false;
      }
    },

    async generateAllTaxDataReport() {
      const response = await API.report.generateAllTaxDataReport();
      this.saveAs(response.data, `all_tax_data.xls`);
    },

    async generateUsageReport() {
      this.$store.dispatch('application/setSuccessNotification', {
        message:
          'Your report is being generated. It will appear in your notifications when it is ready.',
      });
      await API.report.generateUsageReport(this.selectedYear);
    },

    async generateQuestionnaireReport() {
      const response = await API.report.generateQuestionnaireReport(
        this.selectedQuestionnaireYear,
      );
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Successfully queued report. You will get a notification when it is ready.',
        });
      }
    },

    async generateNearlyFinishedQuestionnaireReport() {
      const response = await API.report.generateNearlyFinishedQuestionnaireReport(
        this.selectedQuestionnaireYear,
        this.selectedQuestionnairePercentage,
      );

      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Successfully queued report. You will get a notification when it is ready.',
        });
      }
    },

    saveAs(uri, filename) {
      const link = document.createElement('a');

      if (typeof link.download === 'string') {
        link.href = uri;
        link.download = filename;
        // Firefox requires the link to be in the body
        document.body.appendChild(link);
        // simulate click
        link.click();
        // remove the link when done
        document.body.removeChild(link);
      } else {
        window.open(uri, '_blank');
      }
    },
    async downloadDocument(doc) {
      try {
        const response1 = await API.report.downloadReportTemplate(doc.name);
        this.downloadDocumentByLink(
          response1.data.document.data.link,
          doc.name,
        );
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem downloading the document.',
        });
      }
    },
    async deleteDocument(doc) {
      try {
        const response = await API.report.deleteReportTemplate(doc.name);
        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: response.data.error,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully deleted.',
          });
          this.getAllReportTemplates();
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem deleting the document.',
        });
      }
    },
    async downloadDocumentByLink(url, name) {
      const response = await this.$http.get(url, {
        responseType: 'arraybuffer',
      });
      const blob = new Blob([response.data], {
        type: this.get(response.headers, 'content-type'),
      });
      const link = document.createElement('a');
      link.href = window.URL.createObjectURL(blob);
      link.download = name;
      link.click();
    },
    async generateClientList() {
      const response = await API.report.generateClientList();
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Your report is being generated. You will be notified when the report is done.',
        });
      }
    },
    async getCreditUsage() {
      const response = await API.report.getCreditUsage(this.selectedYear);
      this.creditUsage = response.data;
      this.creditUsageLoading = false;
    },
    async generateEntityList() {
      const response = await API.report.generateEntityList();

      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Your report is being generated. You will be notified when the report is done.',
        });
      }
    },
    async generateUnseenMessagesReport() {
      const response = await API.report.generateUnseenMessagesReport();

      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Successfully queued report. You will get a notification when it is ready.',
        });
      }
    },
    async generateEntityListWithConversation() {
      this.$store.dispatch('application/setSuccessNotification', {
        message:
          'Your report is being generated. This usually takes less than a minute to complete.',
      });
      const response = await API.report.generateEntityListWithConversation();
      this.saveAs(
        response.data,
        `Entity List with Conversation(${moment().format('LL')}).xls`,
      );
    },
    async generateSignatureRequestReport() {
      this.$store.dispatch('application/setSuccessNotification', {
        message:
          'Your report is being generated. This usually takes less than a minute to complete.',
      });
      const response = await API.report.generateSignatureRequestReport();
      this.saveAs(
        response.data,
        `Signature Request List (${moment().format('LL')}).xls`,
      );
    },
    async generateDeliveryReport() {
      const response = await API.report.generateDeliveryReport();
      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message: response.data.error,
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'Successfully queued report. You will get a notification when it is ready.',
        });
      }
    },
    async addFile(e) {
      this.documentImporting = true;
      const droppedFiles = e.dataTransfer.files[0];
      if (!droppedFiles) return;

      this.documentToImport = droppedFiles;
      this.documentImporting = false;
    },
    removeFile(file) {
      this.files = this.files.filter(f => {
        return f !== file;
      });
    },
    pickDocument() {
      const input = this.$refs.selectedDocument;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedDocument.click();
    },
    async onDocumentPicked(e) {
      this.documentImporting = true;
      const document = e.target.files[0];

      if (document) {
        this.documentToImport = document;
        this.documentImporting = false;
      }
    },
    async getAllLinkedAccounts() {
      this.linkedAccountsTableLoading = true;
      const response = await API.firm.getAllLinkedAccounts();
      this.linkedAccounts = response.data.accounts;

      this.linkedAccountsTableLoading = false;
    },
    async getAllWebhooks() {
      this.webhooksTableLoading = true;
      const response = await API.integration.getAllWebhooks();
      this.webhooks = response.data.webhooks;

      this.webhooksTableLoading = false;
    },
    async deleteWebhook(id) {
      await API.integration.deleteWebhook({ id });
      await this.getAllWebhooks();
    },
    async getAllReportTemplates() {
      this.reportTemplatesLoading = true;
      const response = await API.report.getAllReportTemplates();
      this.reportTemplates = response.data;
      this.reportTemplatesLoading = false;
    },
    async uploadReportTemplate() {
      this.documentImporting = true;
      try {
        const {
          data: { id, upload_link },
        } = await API.report.getTemporaryUploadLinkForReportTemplate();

        await API.integration.uploadToDropbox(
          upload_link,
          this.documentToImport,
        );

        const response = await API.report.uploadReportTemplate({
          id,
          documentName: this.documentToImport.name,
        });

        if (response.data.error) {
          this.$store.dispatch('application/setErrorNotification', {
            message: `There was a problem uploading the template.`,
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Successfully uploaded.',
          });
          this.getAllReportTemplates();
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was a problem uploading the file',
        });
      } finally {
        this.uploadReportTemplateDialogOpened = false;
        this.documentImporting = false;
        this.documentToImport = [];
      }
    },
    updateSupportEmail(e) {
      this.currentFirm.preferences.support_email = e.target.value;
      this.updatePreferences();
    },
    updatePhoneNumber(e) {
      this.currentFirm.preferences.phone_number = e.target.value;
      this.updatePreferences();
    },
    updateShowPhoneNumber(val) {
      this.currentFirm.preferences.show_phone_number = val;
      this.showPhoneNumberValue = val;
      this.updatePreferences();
    },
    updateHideHelloSorabanFromEmails(val) {
      this.currentFirm.preferences.hide_reply_to_ref_from_emails = val;
      this.hideHelloSorabanFromEmails = val;
      this.updatePreferences();
    },
    updateHideName(val) {
      this.currentFirm.preferences.hide_name_from_email = val;
      this.hideFirmNameFromEmailValue = val;
      this.updatePreferences();
    },
    updateReminderFrequency(e) {
      this.currentFirm.preferences.questionnaire_reminder_frequency =
        e.target.value;
      this.updatePreferences();
    },
    updateCustomWelcomeMessage(val) {
      this.currentFirm.preferences.custom_welcome_message = val;
    },
    updateExtensionEmail(val) {
      this.currentFirm.preferences.extension_email = val;
    },
    async getOrCreateZapierAPIKey() {
      try {
        const response = await API.integration.getOrCreateZapierAPIKey();
        this.zapierAPIKey = response.data.api_key;
      } catch (e) {
        console.log(e);
      }
    },
    capitalizeFirstLetter(string) {
      return string.charAt(0).toUpperCase() + string.slice(1);
    },
    selectImage() {
      const input = this.$refs.selectedLogo;
      input.type = 'text';
      input.type = 'file';
      this.$refs.selectedLogo.click();
    },
    async onFileChange(e) {
      try {
        this.loading = true;
        const logo = e.target.files[0];

        const acceptableFileTypes = [
          'image/png',
          'image/gif',
          'image/jpg',
          'image/jpeg',
          'image/pjpeg',
          'image/tiff',
          'image/bmp',
          'image/vnd.adobe.photoshop',
          'image/vnd.microsoft.icon',
          'image/webp',
          'image/avif',
          'image/heic',
          'image/heif',
        ];

        if (!acceptableFileTypes.includes(logo.type)) {
          return this.$store.dispatch('application/setErrorNotification', {
            message:
              'You cannot upload that file type. Please upload a png, jpg, jpeg, heic, or heif file.',
          });
        }

        const formData = new FormData(this.logoForm);
        formData.append('image', logo);
        // this.item.imageUrl = URL.createObjectURL(file)

        await API.firm.updateCurrentUserFirmLogo(formData);
      } catch (e) {
      } finally {
        this.loading = false;
      }
    },
    async updateSenderEmailAddress() {
      try {
        if (
          this.currentFirm.default_sender_email_address ===
          'accountant_email_address'
        ) {
          this.senderEmailAddressConfirmationDialogOpened = true;
        }

        const response = await API.firm.updateCurrentUserFirm(this.currentFirm);
        if (response.data.error) {
          console.log(response.data);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was an issue.',
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was an issue.',
        });
      }
    },
    async updatePreferences() {
      try {
        const response = await API.firm.updateCurrentUserPreferences(
          this.currentFirm,
        );
        if (response.data.error) {
          console.log(response.data);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was an issue.',
          });
        } else {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'Saved!',
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was an issue.',
        });
      }
    },
    async updateFirm() {
      try {
        const response = await API.firm.updateCurrentUserFirm(this.currentFirm);
        if (response.data.error) {
          console.log(response.data);
          this.$store.dispatch('application/setErrorNotification', {
            message: 'There was an issue.',
          });
        }
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was an issue.',
        });
      }
    },
    async downloadAllFolders(perCompany = false) {
      try {
        await API.firm.downloadFirmZipFolder(perCompany);
        this.$store.dispatch('application/setSuccessNotification', {
          message:
            'You will get a notification once the download is ready, but because it will take a long time, you will receive an email.',
        });
      } catch (e) {
        console.log(e);
        this.$store.dispatch('application/setErrorNotification', {
          message: 'There was an issue.',
        });
      }
    },
    activateStep(incomingHash) {
      switch (incomingHash) {
        case '#onboarding-add-team-member':
          this.activeTab = 'firm-users';
          break;
        case '#onboarding-firm-email-integration':
          this.activeTab = 'integrations';
          break;
        case '#onboarding-upload-contact-sheet':
          this.activeTab = 'reports';
          break;
        case '#onboarding-import-rollover-data':
          this.activeTab = 'operations';
          break;
        default:
          this.activeTab = 'firm-settings';
          break;
      }
    },
    async connectMicrosoftAccount() {
      const response = await API.linkedAccount.getAuthorizationUrl({
        microsoftPermissionsUsageCategory: 'file_management',
        redirectUri: process.env.API_URL + '/linked_accounts/oauth_callback',
        sourceUri: window.location.origin + window.location.pathname,
      });

      const authorizationUrl =
        response.data.authorization_url + '&prompt=select_account';

      window.location.href = authorizationUrl;

      this.$emit('update:opened', false);
    },
    async getStripeOAuthURL() {
      const response = await API.integration.getStripeOAuthURL();
      this.stripeOAuthURL = response.data.stripe_oauth_url;
    },
    async checkStripeOAuthCallback() {
      const { code } = this.$route.query;
      if (code) {
        this.$router.replace('/firm-settings');
        await this.stripeOAuthCallback({ code });
      }
    },
    async stripeOAuthCallback({ code }) {
      const response = await API.integration.stripeOAuthCallback({ code });

      if (response.data.error) {
        this.$store.dispatch('application/setErrorNotification', {
          message:
            'Sorry, there was an error connecting to Stripe. Please try again.',
        });
      } else {
        this.$store.dispatch('application/setSuccessNotification', {
          message: 'Your Stripe account was successfully connected.',
        });
      }
    },
    async startSharepointSync() {
      this.startedSharepointSync = true;

      try {
        const response = await API.integration.resyncSharepoint();

        if (response.status === 200) {
          this.$store.dispatch('application/setSuccessNotification', {
            message: 'The SharePoint sync has successfully started.',
          });
        } else {
          this.startedSharepointSync = false;
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'There was a problem starting the sync but we have been notified.',
          });
        }
      } catch (err) {
        if (
          err.response &&
          err.response.status === 422 &&
          err.response.data &&
          err.response.data.error
        ) {
          this.$store.dispatch('application/setErrorNotification', {
            message: err.response.data.error,
          });
        } else {
          this.startedSharepointSync = false;
          this.$store.dispatch('application/setErrorNotification', {
            message:
              'There was a problem starting the sync but we have been notified.',
          });
        }
      }

      this.syncSharepointIntegrationDialogOpened = false;
    },
  },

  watch: {
    $route: function({ hash }) {
      this.activateStep(hash);
    },
    activeTab: {
      handler() {
        if (this.activeTab === 'reports') {
          this.getCreditUsage();
        }
      },
    },
    selectedYear: {
      handler() {
        this.getCreditUsage();
      },
    },
  },

  async created() {
    const { hash } = this.$route;
    this.activateStep(hash);

    if (this.currentUser.firm_access !== 'partner') {
      this.$store.dispatch('application/setErrorNotification', {
        message: 'You do not have access',
      });

      return this.$router.push('/overview');
    }
    this.$store.dispatch('setCurrentFirm');
    this.hideFirmNameFromEmailValue = this.currentFirm.preferences.hide_name_from_email;
    this.showPhoneNumberValue = this.currentFirm.preferences.show_phone_number;
    this.hideHelloSorabanFromEmails = this.currentFirm.preferences.hide_reply_to_ref_from_emails;
    this.getAllFirmUsers();
    this.getAllReportTemplates();

    await this.getAllLinkedAccounts();
    await this.getAllWebhooks();

    this.checkSharepointIntegration();

    await this.checkStripeOAuthCallback();
    await this.checkStripeIntegration();
    if (!this.stripeStandardConnected) {
      this.getStripeOAuthURL();
    }
  },
};
</script>

<style scoped lang="css">
.firm-logo {
  max-height: 40px;
  max-width: 200px;
  object-fit: contain;
  border: 1px solid #efefef;
}
</style>

<style lang="css">
.user-panel {
  border: 1px solid #bdbdbd;
}
.notification__text {
  color: #616161;
  font-size: 14px;
}
.notification__time {
  color: #757575;
}
.users-overview-data-table tbody td:nth-child(2),
.users-overview-data-table tbody td:nth-child(5) {
  padding-left: 5px;
  padding-right: 5px;
}
.users-overview-data-table tbody td:nth-child(7) {
  padding-left: 0px;
  padding-right: 5px;
}

.title {
  font-weight: bold;
}

.provider-logo {
  width: 24px;
  height: 24px;
}
.drag-and-drop {
  height: 140px;
  border-radius: 4px;
  background: #eee;
  border: 1px solid rgba(0, 0, 0, 0.15);

  i {
    color: rgba(0, 0, 0, 0.3);
  }

  span {
    color: rgba(0, 0, 0, 0.6);
  }

  span.clickable {
    cursor: pointer;
  }
}
.subheading-container {
  display: flex;
  flex-direction: column;
  text-align: left;
}
.subheading-explanation {
  color: var(--gray-800);
  line-height: 16px;
  margin-left: 12px;
  max-width: 600px;
}
.one-drive-integration-layout {
  width: 100%;
}

.bordered-container {
  border: 1px solid #e1e1e1;
  border-radius: 8px;
  padding: 16px;
}
</style>
